import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { mergePages, useFileList } from "@/api/queries";
import { AuthGate } from "@/components/AuthGate";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { DropZone } from "@/components/DropZone";
import { FileList } from "@/components/FileList";
import { LoadMore } from "@/components/LoadMore";
import { NewFolderDialog } from "@/components/NewFolderDialog";
import { UploadButton } from "@/components/UploadButton";

export const Route = createFileRoute("/")({
  component: function RootFolderView() {
    return (
      <AuthGate>
        <RootFolderContent />
      </AuthGate>
    );
  },
});

function RootFolderContent() {
  const { t } = useTranslation();
  const listing = useFileList(null);
  const items = useMemo(() => mergePages(listing.data?.pages), [listing.data]);

  // Mutations (upload, rename, move, delete, new folder) invalidate
  // the ["files"] query family themselves; no manual refetch wiring.
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Breadcrumbs path={null} />
        <div className="flex items-center gap-2">
          <NewFolderDialog parentId={null} />
          <UploadButton parentId={null} />
        </div>
      </div>
      {listing.isPending ? (
        <p className="text-sm text-muted-foreground" role="status">
          {t("files.loading")}
        </p>
      ) : listing.isError ? (
        <p className="text-sm text-destructive" role="alert">
          {listing.error instanceof Error
            ? listing.error.message
            : t("files.error")}
        </p>
      ) : (
        <DropZone parentId={null}>
          {items.length === 0 ? (
            <p className="mt-8 text-center text-sm text-muted-foreground">
              {t("files.empty_root")}
            </p>
          ) : (
            <>
              <FileList items={items} />
              <LoadMore
                hasNextPage={listing.hasNextPage}
                isFetchingNextPage={listing.isFetchingNextPage}
                onLoadMore={() => void listing.fetchNextPage()}
              />
            </>
          )}
        </DropZone>
      )}
    </div>
  );
}
